import { useEffect, useMemo, useState } from 'react';
import {
  SavingEvent,
  ToolbarPreparingEvent,
  DataGrid,
  DataSourceLike,
} from './dx-grid';

export function getAllRows<T>(ev: SavingEvent<T, string>): T[] {
  const data: Record<string, T> = {};
  ev.component
    .getDataSource()
    .items()
    .forEach((item: T) => {
      data[(item as any).id] = item;
    });
  ev.changes.forEach((changedItem) => {
    if (changedItem.type === 'remove') {
      delete data[changedItem.key];
    } else if (changedItem.type === 'update') {
      data[changedItem.key] = {
        ...data[changedItem.key],
        ...(changedItem.data as T),
      };
    } else {
      data[changedItem.key] = changedItem.data as T;
    }
  });
  const allRows = Object.values(data);
  return allRows;
}

/*
 *  This hook returned cached dataSource if DataGrid is in editing state.
 *  Otherwise it returns updated DataSource
 *  We shouldn't update DataSource if we are editing the data, otherwise the changes will be lost.
 *  Another case is when you add 2 rows, and one of the rows is failing server-side validation:
 *    in this case that row should stay in edit state (but if Data Source is updated, such row is removed)
 */
export function useCachedDataSourceWhenEditing<T, TKey>(
  dataGridRef: React.RefObject<DataGrid<T, TKey>>,
  dataSource: DataSourceLike<T, TKey> | undefined,
) {
  const [cachedDataSource, setCachedDataSource] = useState(dataSource ?? []);
  useEffect(() => {
    if (dataGridRef.current?.instance().hasEditData()) return;
    setCachedDataSource(dataSource ?? []);
  }, [dataSource]);
  return cachedDataSource;
}

export function allowOnlySingleNewRow(e: ToolbarPreparingEvent<any>) {
  e.toolbarOptions!.items!.forEach(function (item) {
    if (item.name === 'addRowButton') {
      const handler = item.options.onClick;

      // implement new handler
      item.options.onClick = function (x: any) {
        const isNewRowExist = e.element.innerHTML.includes('dx-row-inserted');
        // if there are already new rows, do not call the handler
        if (!isNewRowExist) handler(x);
      };
    }
  });
}

/*
 * This hook could be used to show loading on Adding/Changing/Deleting in DevExpress DataGrids.
 * It takes into account that several Adding/Changing/Deleting operations could run in parallel.
 */
export function useParallelSaving<T, TKey>(
  dataGridRef?: React.RefObject<DataGrid<T, TKey>>,
): {
  isLoading: boolean;
  startLoading: () => () => void;
} {
  const [isLoading, setIsLoading] = useState(false);
  const startLoading = useMemo(() => {
    let parallelSavingCount = 0;
    return () => {
      parallelSavingCount++;
      if (parallelSavingCount === 1) {
        setIsLoading(true);
        dataGridRef?.current?.instance().beginCustomLoading('');
      }
      return () => {
        parallelSavingCount--;
        if (parallelSavingCount === 0) {
          setIsLoading(false);
          dataGridRef?.current?.instance().endCustomLoading();
        }
      };
    };
  }, []);
  return { isLoading, startLoading };
}
